function createOverviewElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createStatusDot(status) {
  const value = String(status || 'unknown');
  const tone = value === 'ok' || value === 'success'
    ? 'ok'
    : ['degraded', 'warn', 'warning', 'unconfigured', 'medium'].includes(value)
      ? 'warn'
      : value === 'unknown' ? '' : 'bad';
  return createOverviewElement('i', `cc-dot ${tone}`.trim());
}

function createCard(label, extraClass = '') {
  const card = createOverviewElement('div', `cc-card${extraClass ? ` ${extraClass}` : ''}`);
  card.append(createOverviewElement('div', 'label', label));
  return card;
}

function createCardHead(title, hint, action = null) {
  const head = createOverviewElement('div', 'cc-card-head');
  head.append(createOverviewElement('h3', '', title));
  if (action) {
    head.append(action);
  } else {
    head.append(createOverviewElement('span', 'cc-hint', hint));
  }
  return head;
}

function createMetric(label, value, foot = '', tone = '', unavailable = false) {
  const card = createCard(label, 'metric');
  const valueElement = createOverviewElement('div', `value${unavailable ? ' cc-na' : ''}`, value);
  card.append(valueElement);
  if (foot) card.append(createOverviewElement('div', `foot${tone ? ` ${tone}` : ''}`, foot));
  return card;
}

function createProbe(name, probe, helpers, unavailableReason = '') {
  const card = createOverviewElement('div', 'cc-probe');
  const state = createOverviewElement('div', 'state');
  card.append(createOverviewElement('div', 'name', helpers.serviceLabel(name)));
  if (!probe) {
    state.append(createStatusDot('unknown'), createOverviewElement('span', 'cc-na', helpers.unavailableLabel(unavailableReason || '暂无可用数据')));
    card.append(state, createOverviewElement('div', 'meta', name));
    return card;
  }
  state.append(createStatusDot(probe.status), document.createTextNode(helpers.labelStatus(probe.status)));
  card.append(
    state,
    createOverviewElement('div', 'meta', `${name} · ${probe.detail || ''} · ${helpers.ms(probe.latencyMs)}`),
  );
  return card;
}

function createDependencyList(providers, limit, helpers) {
  const list = createOverviewElement('div', 'cc-dep-list');
  const rows = (providers || []).slice(0, limit);
  if (!rows.length) {
    list.append(createOverviewElement('div', 'muted', '当前时间范围内暂无依赖调用样本'));
    return list;
  }
  rows.forEach((provider) => {
    const failRate = Number(provider.failRate || 0);
    const width = Math.min(100, Math.max(6, failRate * 400));
    const row = createOverviewElement('div', 'cc-dep-row');
    const name = createOverviewElement('strong', '', helpers.providerLabel(provider.provider));
    name.title = provider.provider || '';
    const meter = document.createElement('progress');
    meter.className = `cc-progress-meter${failRate >= 0.05 ? ' bad' : ''}`;
    meter.max = 100;
    meter.value = width;
    row.append(
      name,
      createOverviewElement('span', failRate >= 0.05 ? 'delta-down' : 'muted', helpers.pct(failRate)),
      createOverviewElement('span', 'mono', helpers.ms(provider.p90Ms ?? provider.avgMs)),
      meter,
    );
    list.append(row);
  });
  return list;
}

function createHttpChart(points) {
  const chart = createOverviewElement('div', 'cc-chart');
  if (!points.length) {
    chart.append(createOverviewElement('div', 'muted cc-chart-empty', '当前时间范围内暂无 HTTP 时序点'));
    return chart;
  }
  const width = 640;
  const height = 160;
  const padding = 7;
  const pathFor = (values) => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    return values.map((value, index) => {
      const x = padding + (index * (width - padding * 2)) / Math.max(1, values.length - 1);
      const y = height - padding - ((value - min) / span) * (height - padding * 2);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  };
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('aria-hidden', 'true');
  [
    [points.map((point) => Number(point.p90Ms ?? point.avgMs ?? 0)), 'var(--amber,#f59e0b)', '2.5', '1'],
    [points.map((point) => Number(point.qps || 0)), '#6366f1', '2', '.85'],
    [points.map((point) => Number(point.error5xxRate || 0)), 'var(--red,#ef4444)', '2', '.8'],
  ].forEach(([values, stroke, strokeWidth, opacity]) => {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathFor(values));
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', stroke);
    path.setAttribute('stroke-width', strokeWidth);
    path.setAttribute('opacity', opacity);
    svg.append(path);
  });
  chart.append(svg);
  return chart;
}

function createLegend() {
  const legend = createOverviewElement('div', 'cc-legend');
  [['latency', '平均耗时'], ['qps', 'QPS'], ['error', '5xx 率']].forEach(([tone, label]) => {
    legend.append(createOverviewElement('span', `cc-legend-item ${tone}`, label));
  });
  return legend;
}

function createTodos(data, stuckCount) {
  if (!data.todos?.available) return createOverviewElement('span', 'cc-na', '暂无可用数据');
  const list = createOverviewElement('div', 'cc-todo-list');
  [
    ['待审核成员', data.todos.approvalsPending || 0, ''],
    ['待处理反馈', data.todos.feedbackPending || 0, ''],
    ['今日系统异常', data.todos.errorsToday ?? '—', ''],
    ['僵死批量任务', stuckCount ?? '—', Number(stuckCount) > 0 ? 'delta-warn' : ''],
  ].forEach(([label, value, tone]) => {
    const todo = createOverviewElement('div', 'cc-todo');
    todo.append(createOverviewElement('span', '', label), createOverviewElement('strong', tone, String(value)));
    list.append(todo);
  });
  return list;
}

function createAgentRunHealth(data, helpers) {
  const agentRuns = data.agentRunHealth;
  if (!agentRuns?.available) return null;
  const section = createOverviewElement('div', 'cc-section');
  const head = createOverviewElement('div', 'cc-section-head');
  head.append(
    createOverviewElement('h3', '', 'Agent Run 健康'),
    createOverviewElement('span', 'cc-hint', `近 ${agentRuns.sinceMinutes} 分钟 · 只读诊断`),
  );
  const counts = createOverviewElement('div', 'cc-grid-4');
  counts.append(
    createMetric('活动 Run', String(agentRuns.activeCount || 0), 'QUEUED + RUNNING'),
    createMetric('疑似卡住', String(agentRuns.staleCount || 0), agentRuns.staleCount ? '请到 Agent 运营中心查看' : '当前未发现卡住', agentRuns.staleCount ? 'delta-warn' : 'delta-up'),
    createMetric('状态不一致', String(agentRuns.mismatchCount || 0), agentRuns.mismatchCount ? '消息与 Run 需核对' : '当前未发现不一致', agentRuns.mismatchCount ? 'delta-warn' : 'delta-up'),
    createMetric('已完成', String(agentRuns.counts?.COMPLETED || 0), '近窗完成数'),
  );
  const metrics = agentRuns.metrics || {};
  const rates = createOverviewElement('div', 'cc-grid-4 cc-agent-run-metrics');
  rates.append(
    createMetric('成功率', metrics.successRate == null ? '—' : helpers.pct(metrics.successRate), '终态 Run'),
    createMetric('取消率', metrics.cancellationRate == null ? '—' : helpers.pct(metrics.cancellationRate), '终态 Run'),
    createMetric('质量通过率', metrics.qualityPassRate == null ? '—' : helpers.pct(metrics.qualityPassRate), '已评估 Run'),
    createMetric('P95 耗时', metrics.p95DurationMs == null ? '—' : helpers.ms(metrics.p95DurationMs), `P99 ${metrics.p99DurationMs == null ? '—' : helpers.ms(metrics.p99DurationMs)}`),
  );
  section.append(head, counts, rates);
  return section;
}

export function renderCommandCenterOverview(root, data, performance, helpers) {
  if (!root) return;
  const probes = data.probes?.probes || {};
  const dependencyData = performance?.dependencies?.available ? performance.dependencies : data.dependencies;
  const providers = dependencyData?.providers || [];
  const imageProvider = providers.find((provider) => /linkfox|image|ziniao/i.test(provider.provider));
  const http = performance?.http?.available ? performance.http : data.http;
  const rateLimits = performance?.rateLimits?.available ? performance.rateLimits : data.rateLimits;
  const windowLabel = `${performance?.windowMinutes || 60} 分钟`;
  const alerts = data.alerts?.items || [];
  const stuckCount = data.operationalHealth?.stuckCount;

  const nodes = [];
  if (alerts[0]) {
    const banner = createOverviewElement('div', 'cc-alert-banner');
    const detail = document.createElement('div');
    detail.append(
      createOverviewElement('strong', '', alerts[0].message || alerts[0].rule || ''),
      createOverviewElement('p', '', alerts.length > 1 ? `另有 ${alerts.length - 1} 条告警` : '见下方列表'),
    );
    banner.append(createOverviewElement('span', 'cc-tag bad', '触发中'), detail);
    nodes.push(banner);
  }

  const health = createOverviewElement('div', 'cc-section');
  const healthHead = createOverviewElement('div', 'cc-section-head');
  healthHead.append(createOverviewElement('h3', '', '平台健康'), createOverviewElement('span', 'cc-hint', '真探针 + 近窗依赖指标'));
  const probeGrid = createOverviewElement('div', 'cc-probe-grid');
  ['app', 'mysql', 'redis', 'sif', 'selectdb'].forEach((name) => probeGrid.append(createProbe(name, probes[name], helpers, data.probes?.available === false ? data.probes?.reason || 'admin_only' : '')));
  if (imageProvider) {
    const probe = createOverviewElement('div', 'cc-probe');
    const state = createOverviewElement('div', 'state');
    const failed = imageProvider.failRate > 0.05;
    state.append(createStatusDot(failed ? 'bad' : 'ok'), document.createTextNode(failed ? '异常' : '正常'));
    probe.append(
      createOverviewElement('div', 'name', '商品套图'),
      state,
      createOverviewElement('div', 'meta', `linkfox · 失败率 ${helpers.pct(imageProvider.failRate)} · ${helpers.ms(imageProvider.avgMs)}`),
    );
    probeGrid.append(probe);
  }
  health.append(healthHead, probeGrid);
  nodes.push(health);

  const metrics = createOverviewElement('div', 'cc-grid-4');
  const httpReady = Boolean(http?.available);
  const rateLimitReady = Boolean(rateLimits?.available);
  metrics.append(
    createMetric('HTTP p90', httpReady ? helpers.ms(http.p90Ms ?? http.avgMs) : helpers.unavailableLabel(http?.reason || 'table_missing'), httpReady ? `${windowLabel} · ${Number(http.total || 0)} 次请求` : '', '', !httpReady),
    createMetric('HTTP 5xx 率', httpReady ? helpers.pct(http.error5xxRate) : helpers.unavailableLabel(http?.reason || 'table_missing'), httpReady ? (Number(http.error5xxRate || 0) > 0.01 ? '超过 1% 告警阈值' : '运行在告警阈值内') : '', Number(http?.error5xxRate || 0) > 0.01 ? 'delta-down' : 'delta-up', !httpReady),
    createMetric('僵死批量任务', stuckCount == null ? helpers.unavailableLabel('暂无可用数据') : String(stuckCount), stuckCount == null ? '' : (Number(stuckCount) > 0 ? '需要进入任务与队列诊断' : '当前未发现占用并发的任务'), Number(stuckCount) > 0 ? 'delta-warn' : 'delta-up', stuckCount == null),
    createMetric('限流拒绝', rateLimitReady ? String(rateLimits.total || 0) : helpers.unavailableLabel(rateLimits?.reason || 'not_ready'), rateLimitReady ? `${windowLabel}累计` : '', Number(rateLimits?.total || 0) > 20 ? 'delta-warn' : '', !rateLimitReady),
  );
  nodes.push(metrics);

  const agentRunHealth = createAgentRunHealth(data, helpers);
  if (agentRunHealth) nodes.push(agentRunHealth);

  const trafficGrid = createOverviewElement('div', 'cc-grid-2');
  const trafficCard = createOverviewElement('div', 'cc-card');
  trafficCard.append(createCardHead('HTTP 近窗趋势', '延迟 · QPS · 5xx'), createHttpChart(performance?.series?.points || []), createLegend());
  const dependenciesCard = createOverviewElement('div', 'cc-card');
  dependenciesCard.append(createCardHead('依赖失败 Top', '失败率 / p90'));
  if (dependencyData?.available) {
    dependenciesCard.append(createDependencyList(providers, 5, helpers));
  } else {
    dependenciesCard.append(createOverviewElement('span', 'cc-na', helpers.unavailableLabel(dependencyData?.reason)));
  }
  trafficGrid.append(trafficCard, dependenciesCard);
  nodes.push(trafficGrid);

  const focusGrid = createOverviewElement('div', 'cc-grid-2');
  const focusCard = createOverviewElement('div', 'cc-card');
  focusCard.append(createCardHead('当前运行焦点', '阈值告警与运行风险'));
  const alertList = createOverviewElement('div', 'cc-list');
  if (alerts.length) {
    alerts.forEach((alert) => {
      const high = alert.severity === 'high';
      const row = createOverviewElement('div', 'cc-row');
      row.append(
        createStatusDot(high ? 'bad' : 'warn'),
        createOverviewElement('div', '', alert.message || alert.rule || ''),
        createOverviewElement('span', `cc-tag ${high ? 'bad' : 'warn'}`, alert.severity || ''),
      );
      alertList.append(row);
    });
  } else {
    alertList.append(createOverviewElement('div', 'muted', '当前无触发告警'));
  }
  focusCard.append(alertList);
  if (data.singleReplicaWarning) focusCard.append(createOverviewElement('div', 'cc-banner cc-banner-spaced', '当前共享运行态未完全就绪，多实例队列统计可能降级为本机视角。'));

  const rightStack = createOverviewElement('div', 'cc-stack');
  const usage = createOverviewElement('div', 'cc-card');
  usage.append(createCardHead('今日调用', '成本明细请查看消耗模块'));
  if (data.costToday?.available) {
    usage.append(
      createOverviewElement('div', 'value', String(Number(data.costToday.requestCount || 0))),
      createOverviewElement('div', 'foot', `今日调用次数 · ${Number(data.costToday.tokens || 0).toLocaleString('zh-CN')} tokens`),
    );
  } else {
    usage.append(createOverviewElement('div', 'value cc-na', helpers.unavailableLabel(data.costToday?.reason || 'not_ready')));
  }
  const todos = createOverviewElement('div', 'cc-card');
  const taskButton = createOverviewElement('button', 'btn btn-sm', '查看任务');
  taskButton.type = 'button';
  taskButton.dataset.ccPanelGo = 'tasks';
  todos.append(createCardHead('待办', '', taskButton), createTodos(data, stuckCount));
  rightStack.append(usage, todos);
  focusGrid.append(focusCard, rightStack);
  nodes.push(focusGrid);

  root.replaceChildren(...nodes);
}
